import { useState } from 'react'
import { Link } from '@tanstack/react-router'

import { ConnectGitHubPrompt } from '@/components/integration/ConnectGitHubGate'
import { LatencyDistribution } from '@/components/metrics/LatencyDistribution'
import { StatRow, StatTile } from '@/components/metrics/StatTile'
import { ToolPerformanceTable } from '@/components/metrics/ToolPerformanceTable'
import { Select } from '@/components/ui/Controls'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { EmptyState, QueryBoundary, Skeleton } from '@/components/ui/States'
import { useAuth } from '@/features/auth/useAuth'
import {
  useErrorBreakdown,
  useMetricsOverview,
  useToolPerformance,
} from '@/features/metrics/useMetrics'
import { formatCount, formatMs, formatPercent, formatRelative } from '@/lib/utils/format'

const WINDOWS = [
  { value: '1', label: 'last hour' },
  { value: '24', label: 'last 24 hours' },
  { value: '168', label: 'last 7 days' },
  { value: '720', label: 'last 30 days' },
]

export function MetricsPage() {
  const { user } = useAuth()
  const [hours, setHours] = useState(24)
  const overview = useMetricsOverview(hours)
  const tools = useToolPerformance(hours)
  const errors = useErrorBreakdown(hours)

  const totals = overview.data?.totals
  const blocked = (totals?.denied ?? 0) + (totals?.rejected ?? 0) + (totals?.validation_failed ?? 0)

  return (
    <div>
      <PageHeader
        title="Metrics"
        description="What this server did on your behalf, measured from your own execution records."
        actions={
          <Select
            label="window"
            options={WINDOWS}
            value={String(hours)}
            onChange={(event) => setHours(Number(event.target.value))}
          />
        }
      />

      <QueryBoundary
        isLoading={overview.isLoading}
        error={overview.error}
        onRetry={() => void overview.refetch()}
        loadingFallback={<Skeleton className="h-28 w-full" />}
      >
        {() => (
        <StatRow>
          <StatTile
            label="Tool calls"
            value={formatCount(totals?.calls ?? 0)}
            hint="Every invocation, including ones stopped before they reached GitHub."
          />
          <StatTile
            label="Execution success"
            value={formatPercent(totals?.success_rate ?? 0, 0)}
            tone={(totals?.success_rate ?? 1) >= 0.95 ? 'ok' : 'fail'}
            hint="Successes over calls that actually ran. Denials are excluded — they never reached GitHub."
            footer={`${formatCount(totals?.success ?? 0)} succeeded, ${formatCount(totals?.failed ?? 0)} failed`}
          />
          <StatTile
            label="Median latency"
            value={formatMs(overview.data?.latency.p50_ms ?? 0).replace(' ms', '')}
            unit="ms"
            hint="Half of all calls finished faster than this."
            footer={`p95 ${formatMs(overview.data?.latency.p95_ms ?? 0)}`}
          />
          <StatTile
            label="Stopped by safety"
            value={formatCount(blocked)}
            tone={blocked > 0 ? 'denied' : 'default'}
            hint="Denied by policy, or refused for invalid arguments. Neither reached GitHub."
          />
        </StatRow>
        )}
      </QueryBoundary>

      {/* The honest framing: this page measures what happened, not whether the model chose
          well. Selection quality needs ground truth, which only the benchmark has. */}
      <p className="mt-3 text-xs text-ink-muted">
        These are observed operations. Whether the model picked the <em>right</em> tool cannot
        be measured here — that needs an expected answer, which only the{' '}
        <Link to="/benchmark" className="text-primary underline-offset-2 hover:underline">
          benchmark
        </Link>{' '}
        has.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel title="Total latency" description="End to end, measured inside the tool runtime">
          <QueryBoundary
            isLoading={overview.isLoading}
            error={overview.error}
            onRetry={() => void overview.refetch()}
            loadingFallback={<Skeleton className="m-4 h-24" />}
          >
            {() => <LatencyDistribution stats={overview.data!.latency} label="All tool calls" />}
          </QueryBoundary>
        </Panel>

        <Panel title="GitHub latency" description="Time spent waiting on the GitHub API">
          <QueryBoundary
            isLoading={overview.isLoading}
            error={overview.error}
            onRetry={() => void overview.refetch()}
            loadingFallback={<Skeleton className="m-4 h-24" />}
          >
            {() => (
              <LatencyDistribution
                stats={overview.data!.github_latency}
                label="Outbound requests"
                accent="remote"
              />
            )}
          </QueryBoundary>
        </Panel>
      </div>

      <Panel
        title="Per-tool performance"
        description="p95 bars share one scale, so tools are comparable to each other"
        className="mt-4"
      >
        <QueryBoundary
          isLoading={tools.isLoading}
          error={tools.error}
          onRetry={() => void tools.refetch()}
          isEmpty={(tools.data?.tools.length ?? 0) === 0}
          emptyState={
            user?.github_connected ? (
                <EmptyState
              title="No calls in this window."
              hint="Widen the window, or make a request from Chat or a connected MCP host."
            />
              ) : (
                <ConnectGitHubPrompt what="Your tool calls" />
              )
          }
        >
          <ToolPerformanceTable tools={tools.data?.tools ?? []} />
        </QueryBoundary>
      </Panel>

      <Panel
        title="Failures by type"
        description="Normalised error types, which is what makes a failure diagnosable"
        className="mt-4"
      >
        <QueryBoundary
          isLoading={errors.isLoading}
          error={errors.error}
          onRetry={() => void errors.refetch()}
          isEmpty={(errors.data?.errors.length ?? 0) === 0}
          emptyState={<EmptyState title="No failures in this window." />}
        >
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Error type</th>
                  <th scope="col" className="w-24 text-right">
                    Count
                  </th>
                  <th scope="col" className="w-48">
                    Last seen
                  </th>
                  <th scope="col" className="w-64">
                    Example tool
                  </th>
                </tr>
              </thead>
              <tbody>
                {(errors.data?.errors ?? []).map((bucket) => (
                  <tr key={bucket.error_type}>
                    <td className="mono text-fail">{bucket.error_type}</td>
                    <td className="mono text-right text-ink-dim">{bucket.count}</td>
                    <td className="text-ink-muted">{formatRelative(bucket.last_seen_at)}</td>
                    <td className="mono text-ink-muted">{bucket.sample_tool ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </QueryBoundary>
      </Panel>
    </div>
  )
}
